import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../components/layout";
import {
  UserPlus,
  Trophy,
  AtSign,
  Heart,
  MessageCircle,
  Bell,
  Church,
  BookOpen,
  Cross,
  HandHeart,
  CheckCheck,
  ArrowLeft,
  FileText,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../services/api";
import { toast } from "react-toastify";

// ─── Type → icon map ──────────────────────────────────────────────────────────
const typeIconMap = {
  user_registered: { icon: UserPlus },
  comment_on_post: { icon: MessageCircle },
  community_post: { icon: FileText },
  like_post: { icon: Heart },
  follow: { icon: UserPlus },
  mention: { icon: AtSign },
  trophy: { icon: Trophy },
  prayer: { icon: HandHeart },
  church: { icon: Church },
  bible: { icon: BookOpen },
  cross: { icon: Cross },
  system: { icon: Bell },
  new_challenge: { icon: Trophy },
  mentorship: { icon: UserPlus },
  like: { icon: Heart },
  comment_post: { icon: MessageCircle },
};

// ─── Get icon helper ──────────────────────────────────────────────────────────
const getIcon = (type) => typeIconMap[type] || { icon: Bell };

// ─── Relative time helper ─────────────────────────────────────────────────────
const formatRelativeTime = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// ─── Group by date label ──────────────────────────────────────────────────────
const groupByDate = (items) => {
  const todayMs = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
  const yesterdayMs = todayMs - 86400000;

  return items.reduce((groups, notif) => {
    const d = new Date(notif.created_at);
    const dayMs = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const label =
      dayMs === todayMs
        ? "Today"
        : dayMs === yesterdayMs
          ? "Yesterday"
          : d.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });

    if (!groups[label]) groups[label] = [];
    groups[label].push(notif);
    return groups;
  }, {});
};

// ─── API Root for avatars ─────────────────────────────────────────────────────
const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");

// ─── Avatar with initials fallback ────────────────────────────────────────────
const Avatar = ({ src, name }) => {
  const [imgError, setImgError] = useState(false);
  const fullSrc = src?.startsWith("http")
    ? src
    : src
      ? `${API_ROOT}${src}`
      : null;

  if (!imgError && fullSrc && !src?.includes("user_default")) {
    return (
      <img
        src={fullSrc}
        alt={name || "User"}
        onError={() => setImgError(true)}
        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
      />
    );
  }

  const initials = name
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="w-10 h-10 rounded-full bg-[#1F3A60] text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
      {initials || "?"}
    </div>
  );
};

// ─── Helper to parse challenge data ───────────────────────────────────────────
const getChallengeId = (notif) => {
  if (notif.type !== "new_challenge" || !notif.data) return null;
  try {
    const parsed = JSON.parse(notif.data);
    return parsed.challenge_id || null;
  } catch {
    return null;
  }
};

// ─── Single notification row ──────────────────────────────────────────────────
const NotificationRow = React.memo(({ notif, onRead, onJoinChallenge }) => {
  const { icon: IconComponent } = getIcon(notif.type);
  const isUnread = notif.is_read === 0;
  const displayName = notif.actor_name || "";
  const challengeId = getChallengeId(notif);

  const handleRowClick = () => {
    if (isUnread) onRead(notif.id);
  };

  return (
    <div
      onClick={handleRowClick}
      className={`relative flex items-start gap-3 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-[#F8FAFC] transition-all cursor-pointer
        ${isUnread ? "bg-[#1F3A60]/[0.02]" : ""}`}
    >
      {isUnread && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 bg-[#1F3A60] rounded-r-full" />
      )}

      <div className="relative flex-shrink-0">
        <Avatar src={notif.actor_avatar} name={displayName} />
        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#1F3A60] text-white flex items-center justify-center shadow-sm">
          <IconComponent size={11} />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#334155] leading-relaxed">
          <span className="font-semibold text-[#0F1D2E]">{displayName}</span>
          {displayName && (<span className="text-[#7B8794] mx-1.5">.</span>)}
          <span className={`text-[#526071] ${!displayName && 'font-bold'}`}>{notif.message}</span>
        </p>

        {/* Join Challenge Button */}
        {notif.type === "new_challenge" && challengeId && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onJoinChallenge(challengeId, notif.id);
            }}
            className="mt-3 inline-flex items-center gap-2 px-5 py-2 text-sm font-medium bg-[#1F3A60] text-white rounded-xl hover:bg-[#162b4a] transition-all active:scale-95"
          >
            <Trophy size={16} />
            Join Challenge
          </button>
        )}

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2">
          <span className="text-xs text-[#94A3B8]">
            {formatRelativeTime(notif.created_at)}
          </span>
          {isUnread && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#1F3A60]/10 text-[#1F3A60]">
              New
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

NotificationRow.displayName = "NotificationRow";

// ─── Main Notification Page ───────────────────────────────────────────────────
const NotificationPage = () => {
  const queryClient = useQueryClient();
  const QUERY_KEY = "user-notifications";

  const [page, setPage] = useState(1);
  const [allNotifs, setAllNotifs] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch notifications
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [QUERY_KEY, page],
    queryFn: () => api.fetchNotifications(page),
    keepPreviousData: true,
  });

  // Accumulate notifications across pages
  useEffect(() => {
    if (!data?.data) return;
    setAllNotifs((prev) =>
      page === 1 ? data.data : [...prev, ...data.data]
    );
    setHasNext(data.pagination?.hasNext ?? false);
    setTotalCount(data.pagination?.total ?? 0);
  }, [data, page]);

  const unreadCount = allNotifs.filter((n) => n.is_read === 0).length;
  const grouped = groupByDate(allNotifs);

  // Mark single notification as read
  const { mutate: markOneRead } = useMutation({
    mutationFn: (id) => api.markUserNotificationRead(id),
    onSuccess: (_, id) => {
      setAllNotifs((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  // Mark all as read
  const { mutate: markAllRead, isPending: isMarkingAll } = useMutation({
    mutationFn: () => api.markAllUserNotificationsRead(),
    onSuccess: () => {
      setAllNotifs((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  // Join Challenge Mutation
  // Join Challenge Mutation with dynamic response handling
  const { mutate: joinChallenge, isPending: isJoining } = useMutation({
    mutationFn: ({ challengeId }) => api.joinChallenge(challengeId),
    onSuccess: (response, { notificationId }) => {
      // Handle different API responses
      if (response?.success) {
        toast.success(response.message || "Successfully joined the challenge! ");
      } else {
        toast.success(response?.message || "Something went wrong while joining the challenge.");
      }

      // Mark notification as read on success
      if (response?.success && notificationId) {
        markOneRead(notificationId);
      }
    },
    onError: (err) => {
      const errorMessage = err?.response?.data?.message ||
        err?.message ||
        "Failed to join challenge. Please try again.";
      toast.error(errorMessage);
      console.error(err);
    },
  });

  const handleLoadMore = () => {
    if (hasNext && !isFetching) setPage((p) => p + 1);
  };

  const handleJoinChallenge = (challengeId, notificationId) => {
    joinChallenge({ challengeId, notificationId });
  };

  // First load skeleton
  if (isLoading && page === 1) {
    return (
      <DashboardLayout disableScrollRestriction={true}>
        <div className="flex-1 bg-[#f8fafc] h-[calc(100vh-56px)] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="animate-spin text-[#1F3A60]" />
            <p className="text-sm text-[#7B8794]">Loading notifications…</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isError) {
    return (
      <DashboardLayout disableScrollRestriction={true}>
        <div className="flex-1 bg-[#f8fafc] h-[calc(100vh-56px)] flex items-center justify-center">
          <div className="bg-white rounded-2xl border border-[#E8EDF3] p-8 text-center max-w-sm">
            <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell size={24} className="text-red-400" />
            </div>
            <h3 className="text-base font-semibold text-[#0F1D2E] mb-1">
              Failed to load
            </h3>
            <p className="text-sm text-[#7B8794]">{error?.message}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout disableScrollRestriction={true}>
      <div className="flex-1 bg-[#f8fafc] h-[calc(100vh-56px)] overflow-y-auto">
        <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-2 min-w-0">
              <Link
                to="/dashboard"
                className="p-2 hover:bg-white rounded-full transition-colors lg:hidden flex-shrink-0"
              >
                <ArrowLeft size={20} className="text-[#526071]" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-[20px] sm:text-[28px] font-bold text-[#0F1D2E] tracking-tight font-body">
                  Notifications
                </h1>
                <p className="text-xs sm:text-sm text-[#7B8794] mt-0.5 truncate">
                  {unreadCount > 0
                    ? `${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""
                    }`
                    : "All caught up!"}
                </p>
              </div>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                disabled={isMarkingAll}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-[#1F3A60] bg-white rounded-xl hover:bg-gray-50 transition-all border border-[#E8EDF3] disabled:opacity-60 flex-shrink-0"
              >
                {isMarkingAll ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCheck size={16} />
                )}
                <span className="hidden md:inline">Mark all as read</span>
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="space-y-6 pb-6">
            {allNotifs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E8EDF3] p-8 sm:p-12 text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bell size={28} className="text-[#94A3B8]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F1D2E] mb-1">
                  No notifications
                </h3>
                <p className="text-sm text-[#7B8794]">
                  You're all caught up. Check back later.
                </p>
              </div>
            ) : (

              allNotifs.map((notif, idx) => (
                <NotificationRow
                  key={notif.id}
                  notif={notif}
                  // isLast={idx === items.length - 1}
                  onRead={markOneRead}
                  onJoinChallenge={handleJoinChallenge}
                />
              ))
              // Object.entries(grouped).map(([label, items]) => (
              //   <div key={label}>
              //     <h3 className="text-sm font-semibold text-[#7B8794] mb-3 px-1">
              //       {label}
              //     </h3>
              //     <div className="bg-white rounded-2xl border border-[#E8EDF3] overflow-hidden shadow-sm">
              // {allNotifs.map((notif, idx) => (
              //   <NotificationRow
              //     key={notif.id}
              //     notif={notif}
              //     isLast={idx === items.length - 1}
              //     onRead={markOneRead}
              //     onJoinChallenge={handleJoinChallenge}
              //   />
              // ))}
              //     </div>
              //   </div>
              // ))
            )}
          </div>

          {/* Load More */}
          {allNotifs.length > 0 && (
            <div className="mt-4 sm:mt-6 text-center pb-4 space-y-3">
              <p className="text-xs text-[#94A3B8]">
                Showing {allNotifs.length} of {totalCount} notification
                {totalCount !== 1 ? "s" : ""}
              </p>
              {hasNext && (
                <button
                  onClick={handleLoadMore}
                  disabled={isFetching}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-[#1F3A60] bg-white border border-[#E8EDF3] rounded-xl hover:bg-gray-50 transition-all hover:shadow-sm disabled:opacity-60"
                >
                  {isFetching ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      Loading…
                    </>
                  ) : (
                    "Load more"
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NotificationPage;